import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  User,
  Edit2,
  Plus,
  Trash2,
  Building,
  Search,
  Briefcase,
  Users,
  Link2,
  X,
  Mail,
  Phone,
  Save
} from 'lucide-react';
import { personService } from '../../services/api';
import useNotification from '../../hooks/useNotification';
import Notification from '../../components/common/Notification';

const CONTACT_TYPE_OPTIONS = [
  { value: 'email', label: 'Email' },
  { value: 'phone_cell', label: 'Cell Phone' },
  { value: 'phone_home', label: 'Home Phone' },
  { value: 'phone_work', label: 'Work Phone' },
  { value: 'fax', label: 'Fax' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'website', label: 'Website' },
];

const RELATIONSHIP_TYPE_OPTIONS = [
  { value: 'spouse', label: 'Spouse' },
  { value: 'ex_spouse', label: 'Ex-Spouse' },
  { value: 'parent', label: 'Parent' },
  { value: 'child', label: 'Child' },
  { value: 'sibling', label: 'Sibling' },
  { value: 'business_partner', label: 'Business Partner' },
  { value: 'employer', label: 'Employer' },
  { value: 'employee', label: 'Employee' },
  { value: 'other', label: 'Other' },
];

const ROLE_LABELS = {
  opposing_party: 'Opposing Party',
  opposing_counsel: 'Opposing Counsel',
  co_counsel: 'Co-Counsel',
  witness: 'Witness',
  expert_witness: 'Expert Witness',
  judge: 'Judge',
  guardian_ad_litem: 'Guardian ad Litem',
  mediator: 'Mediator',
  insurer: 'Insurer',
  third_party: 'Third Party',
};

const PersonDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { notification, showSuccess, showError, hideNotification } = useNotification();
  const [person, setPerson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Edit mode
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  // Contact add
  const [addingContact, setAddingContact] = useState(false);
  const [newContact, setNewContact] = useState({ contact_type: 'email', value: '', label: '' });

  // Relationship add
  const [addingRelationship, setAddingRelationship] = useState(false);
  const [relForm, setRelForm] = useState({ relationship_type: 'spouse', notes: '' });
  const [relSearchQuery, setRelSearchQuery] = useState('');
  const [relSearchResults, setRelSearchResults] = useState([]);
  const [relSearchLoading, setRelSearchLoading] = useState(false);
  const [relSelectedPerson, setRelSelectedPerson] = useState(null);
  const [relShowDropdown, setRelShowDropdown] = useState(false);
  const relDebounceRef = useRef(null);

  // Delete

  const fetchPerson = useCallback(async () => {
    try {
      const response = await personService.get(id);
      setPerson(response.data);
      setEditForm({
        person_type: response.data.person_type,
        first_name: response.data.first_name || '',
        last_name: response.data.last_name || '',
        company_name: response.data.company_name || '',
        notes: response.data.notes || '',
      });
    } catch (err) {
      console.error('Error fetching person:', err);
      setError('Failed to load person details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPerson();
  }, [fetchPerson]);

  // Relationship person search with debounce
  useEffect(() => {
    if (relDebounceRef.current) clearTimeout(relDebounceRef.current);
    if (relSearchQuery.length < 2 || relSelectedPerson) {
      setRelSearchResults([]);
      setRelShowDropdown(false);
      return;
    }
    relDebounceRef.current = setTimeout(async () => {
      setRelSearchLoading(true);
      try {
        const response = await personService.search(relSearchQuery);
        // Filter out current person
        const filtered = (response.data || []).filter(p => p.id !== id);
        setRelSearchResults(filtered);
        setRelShowDropdown(true);
      } catch (err) {
        console.error('Relationship search failed:', err);
      } finally {
        setRelSearchLoading(false);
      }
    }, 300);
    return () => { if (relDebounceRef.current) clearTimeout(relDebounceRef.current); };
  }, [relSearchQuery, relSelectedPerson, id]);

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await personService.update(id, editForm);
      setIsEditing(false);
      await fetchPerson();
      showSuccess('Person updated');
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to update person');
    }
  };

  const handleAddContact = async () => {
    if (!newContact.value.trim()) return;
    try {
      await personService.addContact(id, {
        contact_type: newContact.contact_type,
        value: newContact.value.trim(),
        label: newContact.label || undefined,
        is_primary: false,
      });
      setAddingContact(false);
      setNewContact({ contact_type: 'email', value: '', label: '' });
      await fetchPerson();
      showSuccess('Contact added');
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to add contact');
    }
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await personService.deleteContact(id, contactId);
      await fetchPerson();
      showSuccess('Contact removed');
    } catch (err) {
      showError('Failed to remove contact');
    }
  };

  const handleAddRelationship = async () => {
    if (!relSelectedPerson) return;
    try {
      await personService.addRelationship(id, {
        person_id_b: relSelectedPerson.id,
        relationship_type: relForm.relationship_type,
        notes: relForm.notes || undefined,
      });
      setAddingRelationship(false);
      setRelForm({ relationship_type: 'spouse', notes: '' });
      setRelSelectedPerson(null);
      setRelSearchQuery('');
      await fetchPerson();
      showSuccess('Relationship added');
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to add relationship');
    }
  };

  const handleDeleteRelationship = async (relationshipId) => {
    try {
      await personService.deleteRelationship(id, relationshipId);
      await fetchPerson();
      showSuccess('Relationship removed');
    } catch (err) {
      showError('Failed to remove relationship');
    }
  };


  const getRelatedPersonId = (rel) => {
    // Determine which side of the relationship is the "other" person
    if (String(rel.person_id_a) === String(id)) return String(rel.person_id_b);
    return String(rel.person_id_a);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  if (error || !person) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 mb-4">
          <User className="h-16 w-16 mx-auto" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Person Not Found</h2>
        <p className="text-gray-600 mb-4">{error || 'The requested person could not be found.'}</p>
        <Link
          to="/persons"
          className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600"
        >
          Back to Persons
        </Link>
      </div>
    );
  }

  const personName = [person.first_name, person.last_name].filter(Boolean).join(' ') || person.company_name || 'Unnamed';
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div>
            <div className="flex items-center space-x-3">
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                {person.person_type === 'individual' ? (
                  <User className="mr-3 h-8 w-8" />
                ) : (
                  <Building className="mr-3 h-8 w-8" />
                )}
                {personName}
              </h1>
              <span className="px-3 py-1 text-sm rounded-full bg-gray-100 text-gray-800 capitalize">
                {person.person_type}
              </span>
            </div>
            <p className="text-gray-600">
              Added {new Date(person.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="bg-yellow-500 text-white px-4 py-2 rounded-lg flex items-center hover:bg-yellow-600"
          >
            <Edit2 className="mr-2 h-4 w-4" />
            Edit Person
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Person Info */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Person Information</h2>

            {isEditing ? (
              <form onSubmit={handleEditSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={editForm.person_type}
                    onChange={(e) => setEditForm({ ...editForm, person_type: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    <option value="individual">Individual</option>
                    <option value="business">Business</option>
                    <option value="organization">Organization</option>
                  </select>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                    <input
                      type="text"
                      value={editForm.first_name}
                      onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                    <input
                      type="text"
                      value={editForm.last_name}
                      onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                  <input
                    type="text"
                    value={editForm.company_name}
                    onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={editForm.notes}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
                <div className="flex space-x-3">
                  <button
                    type="submit"
                    className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600"
                  >
                    Save Changes
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditing(false);
                      setEditForm({
                        person_type: person.person_type,
                        first_name: person.first_name || '',
                        last_name: person.last_name || '',
                        company_name: person.company_name || '',
                        notes: person.notes || '',
                      });
                    }}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-3">
                {person.first_name && (
                  <div className="flex items-center space-x-3">
                    <User className="h-5 w-5 text-gray-400" />
                    <div>
                      <label className="text-sm font-medium text-gray-500">Name</label>
                      <p className="text-gray-900">
                        {[person.first_name, person.last_name].filter(Boolean).join(' ')}
                      </p>
                    </div>
                  </div>
                )}
                {person.company_name && (
                  <div className="flex items-center space-x-3">
                    <Building className="h-5 w-5 text-gray-400" />
                    <div>
                      <label className="text-sm font-medium text-gray-500">Company</label>
                      <p className="text-gray-900">{person.company_name}</p>
                    </div>
                  </div>
                )}
                {person.notes && (
                  <div className="flex items-start space-x-3">
                    <Edit2 className="h-5 w-5 text-gray-400 mt-1" />
                    <div>
                      <label className="text-sm font-medium text-gray-500">Notes</label>
                      <p className="text-gray-900 whitespace-pre-line">{person.notes}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Contact Methods */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Contact Methods</h2>
              <button
                onClick={() => setAddingContact(true)}
                className="text-sm text-yellow-700 hover:text-yellow-800 flex items-center gap-1"
              >
                <Plus size={16} /> Add Contact
              </button>
            </div>

            {person.contacts && person.contacts.length > 0 ? (
              <div className="space-y-2">
                {person.contacts.map((contact) => (
                  <div key={contact.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div className="flex items-center space-x-3">
                      {contact.contact_type === 'email' ? (
                        <Mail className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Phone className="h-4 w-4 text-gray-400" />
                      )}
                      <div>
                        <span className="text-sm text-gray-900">{contact.value}</span>
                        <span className="text-xs text-gray-500 ml-2 capitalize">
                          {contact.contact_type.replace('_', ' ')}
                          {contact.is_primary && ' (primary)'}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteContact(contact.id)}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">No contacts added yet.</p>
            )}

            {addingContact && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg space-y-2">
                <div className="flex items-center gap-2">
                  <select
                    value={newContact.contact_type}
                    onChange={(e) => setNewContact({ ...newContact, contact_type: e.target.value })}
                    className="border border-gray-300 rounded-md px-2 py-1.5 text-sm w-36"
                  >
                    {CONTACT_TYPE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={newContact.value}
                    onChange={(e) => setNewContact({ ...newContact, value: e.target.value })}
                    placeholder={newContact.contact_type === 'email' ? 'email@example.com' : '(555) 123-4567'}
                    className="flex-1 border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => { setAddingContact(false); setNewContact({ contact_type: 'email', value: '', label: '' }); }}
                    className="px-3 py-1 text-xs text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddContact}
                    disabled={!newContact.value.trim()}
                    className="px-3 py-1 text-xs text-white bg-yellow-600 rounded hover:bg-yellow-700 disabled:opacity-50 flex items-center gap-1"
                  >
                    <Save size={12} /> Save
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Relationships */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold flex items-center">
                <Link2 className="mr-2 h-5 w-5" />
                Relationships
              </h2>
              <button
                onClick={() => setAddingRelationship(true)}
                className="text-sm text-yellow-700 hover:text-yellow-800 flex items-center gap-1"
              >
                <Plus size={16} /> Add Relationship
              </button>
            </div>

            {person.relationships && person.relationships.length > 0 ? (
              <div className="space-y-2">
                {person.relationships.map((rel) => (
                  <div key={rel.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div className="flex items-center space-x-3">
                      <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800 capitalize">
                        {rel.relationship_type.replace('_', ' ')}
                      </span>
                      <Link
                        to={`/persons/${getRelatedPersonId(rel)}`}
                        className="text-sm text-yellow-700 hover:text-yellow-800 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {rel.related_person_name || 'Unknown'}
                      </Link>
                      {rel.notes && (
                        <span className="text-xs text-gray-500">({rel.notes})</span>
                      )}
                    </div>
                    <button
                      onClick={() => handleDeleteRelationship(rel.id)}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">No relationships recorded.</p>
            )}

            {addingRelationship && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg space-y-3">
                {/* Person search */}
                <div className="relative">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Related Person</label>
                  {relSelectedPerson ? (
                    <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
                      <span className="text-sm text-blue-800">
                        {[relSelectedPerson.first_name, relSelectedPerson.last_name].filter(Boolean).join(' ')}
                        {relSelectedPerson.company_name && ` (${relSelectedPerson.company_name})`}
                      </span>
                      <button type="button" onClick={() => { setRelSelectedPerson(null); setRelSearchQuery(''); }} className="text-blue-600 hover:text-blue-800">
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={relSearchQuery}
                        onChange={(e) => setRelSearchQuery(e.target.value)}
                        placeholder="Search persons..."
                        className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      />
                      {relSearchLoading && (
                        <div className="absolute right-2.5 top-2">
                          <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-gray-600 rounded-full"></div>
                        </div>
                      )}
                    </div>
                  )}
                  {relShowDropdown && relSearchResults.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-y-auto">
                      {relSearchResults.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => {
                            setRelSelectedPerson(p);
                            setRelShowDropdown(false);
                            setRelSearchQuery('');
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-900">
                            {[p.first_name, p.last_name].filter(Boolean).join(' ')}
                          </div>
                          {p.company_name && (
                            <div className="text-gray-500 text-xs">{p.company_name}</div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Relationship Type</label>
                    <select
                      value={relForm.relationship_type}
                      onChange={(e) => setRelForm({ ...relForm, relationship_type: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                    >
                      {RELATIONSHIP_TYPE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                    <input
                      type="text"
                      value={relForm.notes}
                      onChange={(e) => setRelForm({ ...relForm, notes: e.target.value })}
                      placeholder="Optional notes"
                      className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => {
                      setAddingRelationship(false);
                      setRelForm({ relationship_type: 'spouse', notes: '' });
                      setRelSelectedPerson(null);
                      setRelSearchQuery('');
                    }}
                    className="px-3 py-1 text-xs text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddRelationship}
                    disabled={!relSelectedPerson}
                    className="px-3 py-1 text-xs text-white bg-yellow-600 rounded hover:bg-yellow-700 disabled:opacity-50 flex items-center gap-1"
                  >
                    <Save size={12} /> Save
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Case Appearances */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <Briefcase className="mr-2 h-5 w-5" />
              Case Appearances
            </h2>

            {person.case_parties && person.case_parties.length > 0 ? (
              <div className="space-y-2">
                {person.case_parties.map((cp) => (
                  <div
                    key={cp.id}
                    onClick={() => navigate(`/cases/${cp.case_id}`)}
                    className="flex items-center justify-between py-3 px-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-blue-300 cursor-pointer transition-colors"
                  >
                    <div>
                      <div className="text-sm font-medium text-gray-900">{cp.case_title || 'Untitled Case'}</div>
                      <div className="text-xs text-gray-500">Case #{cp.case_number}</div>
                    </div>
                    <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700">
                      {ROLE_LABELS[cp.role] || cp.role}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <Briefcase className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-500">Not linked to any cases</p>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Users className="mr-2 h-5 w-5" />
              Quick Stats
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Contacts</span>
                <span className="font-medium">{person.contacts?.length || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Relationships</span>
                <span className="font-medium">{person.relationships?.length || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Cases</span>
                <span className="font-medium">{person.case_parties?.length || 0}</span>
              </div>
            </div>
          </div>

        </div>
      </div>

      <Notification
        isOpen={notification.isOpen}
        onClose={hideNotification}
        title={notification.title}
        message={notification.message}
        variant={notification.variant}
        duration={notification.duration}
      />
    </div>
  );
};

export default PersonDetail;
